import { FLAGS } from '@iheartjane/feature-flags';
import { IneligibleDealBanner, WizardButton } from '@iheartjane/menu';
import { Flex, JaneGoldIcon, Typography } from '@iheartjane/reefer';
import { useEffect } from 'react';
import { useLoaderData } from 'react-router';

import { BloomWizardModal } from '@jane/bloom/components/bloomWizardModal';
import { BloomGoldCalloutBanner } from '@jane/bloom/components/janeGold/bloomGoldCalloutBanner';
import { MenuHeader } from '@jane/bloom/components/menuHeader/menuHeader';
import { JaneGoldGrid } from '@jane/bloom/components/menuRow/janeGoldGrid';
import {
  FilterSelector,
  MobileFilterRow,
} from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors';
import { CategoryTab } from '@jane/bloom/components/routes/category/components/categoryTab';
import { SeoHeading } from '@jane/bloom/components/seoHeading';
import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { useMenuTopRowWithServerData } from '@jane/bloom/hooks/dm/useMenuTopRowWithServerData';
import { BloomFilterProvider } from '@jane/bloom/hooks/filters';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useShowInlineWizardCta } from '@jane/bloom/hooks/useShowInlineWizardCta';
import type { RecommendedSortResults } from '@jane/bloom/loaders/products/recommendedSortResults.server';
import { createMetaVariables } from '@jane/bloom/resources/meta/createMetaVariables';
import { DEFAULT_TITLE } from '@jane/bloom/resources/meta/routes/janeGold';
import { WIZARD_SOURCE } from '@jane/bloom/services/tracking/Events';
import { trackSearchedMenuProducts } from '@jane/bloom/services/tracking/track';
import { isJaneUser } from '@jane/bloom/types/schemas/marketplace/janeUser';

import styles from './janeGoldRouteComponent.module.css';

export function JaneGoldRouteComponent() {
  const { featureFlags, metaProps, pinnedStore, theme, whoami } =
    useRootLoaderData();
  const showInlineWizardCta = useShowInlineWizardCta();
  const authenticated = isJaneUser(whoami.user);
  const {
    availableFilters,
    brandSpecials,
    category,
    customCategoryLabels,
    customLanguage,
    enabledSpecialsForRow: enabledStoreSpecials,
    hasLinkedBank,
    janeGold,
    menuProductImgsAboveTheFold,
    menuTopRow,
    recommendedSortResults,
    searchCriteria,
    searchFilterWithoutBrands,
    sort: { currentSort, sortOptions },
    storeId,
  } = useLoaderData<RecommendedSortResults>();
  const rowInstance = useMemoizedRowInstance(recommendedSortResults);

  // Handles server-side initial load and client-side refetching of the menu top row
  const {
    pauseRefetching,
    rowInstance: menuTopRowInstance,
    unpauseRefetching,
  } = useMenuTopRowWithServerData({
    initialMenuTopRow: menuTopRow,
    searchFilter: searchFilterWithoutBrands,
    storeId,
  });

  useEffect(() => {
    if (!rowInstance) return;
    trackSearchedMenuProducts({
      hits: rowInstance?.totalProducts,
      searchCriteria,
      theme,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(searchCriteria), rowInstance?.totalProducts]);

  return (
    <>
      <MenuHeader />
      <SeoHeading
        customSEOPage={{}}
        defaultTitle={DEFAULT_TITLE}
        metaVariables={createMetaVariables(metaProps)}
      />
      <div className={styles['janeGoldSection']}>
        <IneligibleDealBanner />
      </div>
      {brandSpecials.length > 0 && (
        <div className={styles['janeGoldSection']}>
          <Flex alignItems="center" pt={24} mb={16}>
            <JaneGoldIcon color="gold" />
            <Typography as="h2" variant="header-bold" color="text-main" ml={8}>
              Jane Gold rewards
            </Typography>
          </Flex>
          <BloomGoldCalloutBanner
            hasLinkedBank={hasLinkedBank ?? false}
            isSignedIn={!!authenticated}
            storeState={pinnedStore.state}
            mb={24}
          />
          <JaneGoldGrid
            authenticated={authenticated}
            brandSpecials={brandSpecials}
          />
        </div>
      )}
      <BloomFilterProvider
        availableFilters={availableFilters}
        searchCriteria={searchCriteria}
      >
        <BloomWizardModal
          brandSpecials={brandSpecials}
          specials={enabledStoreSpecials}
        >
          {({ openModal }) => {
            const wizardButton = showInlineWizardCta ? (
              <WizardButton
                onClickStart={() => openModal(WIZARD_SOURCE.BUTTON)}
              />
            ) : undefined;

            return (
              <>
                <div className="filters-container">
                  <FilterSelector
                    actionSlot={wizardButton}
                    availableProductsCount={rowInstance?.totalProducts}
                    sortOptions={sortOptions}
                    currentSort={currentSort}
                    shouldShowGold={false}
                    hasJaneGoldProducts={janeGold.hasJaneGoldProducts}
                    showCompare={featureFlags[FLAGS.c1CompareProducts]}
                  />
                </div>

                <MobileFilterRow
                  actionSlot={wizardButton}
                  availableProductsCount={rowInstance?.totalProducts}
                  sortOptions={sortOptions}
                  currentSort={currentSort}
                  shouldShowGold={janeGold.shouldShowGold}
                  hasJaneGoldProducts={janeGold.hasJaneGoldProducts}
                  showCompare={featureFlags[FLAGS.c1CompareProducts]}
                />

                <CategoryTab
                  pauseRefetching={pauseRefetching}
                  unpauseRefetching={unpauseRefetching}
                  brandSpecials={brandSpecials}
                  category={category}
                  customCategoryLabels={customCategoryLabels}
                  customLanguage={customLanguage}
                  menuTopRowInstance={menuTopRowInstance}
                  preloadedProductIds={menuProductImgsAboveTheFold.map(
                    ({ id }) => id
                  )}
                  rowInstance={rowInstance}
                  specials={enabledStoreSpecials}
                  janeGold={janeGold}
                  enabledSpecialsForRow={enabledStoreSpecials}
                />
              </>
            );
          }}
        </BloomWizardModal>
      </BloomFilterProvider>
    </>
  );
}
